import type { EditorState } from 'prosemirror-state'
import { TextSelection } from 'prosemirror-state'
import type { EditorView } from 'prosemirror-view'

export interface ShouldShowFloatingMenuArgs {
  editor: { isEditable: boolean }
  view: Pick<EditorView, 'hasFocus'>
  state: EditorState
}

/**
 * Replaces tiptap's default `shouldShow` for the floating menu, which additionally requires the
 * caret to sit on an *empty* block at the document root. That left a dead zone: with a collapsed
 * caret on a line that already has text, neither menu appeared — the bubble menu needs a selection
 * — so there was no way to reach the template-tag picker without first selecting something.
 *
 * Here the only requirement is a collapsed text caret in an editable, focused editor. Non-empty
 * selections are deliberately excluded: those belong to the bubble menu, and showing both at once
 * would stack two popups over the same text.
 */
export function shouldShowFloatingMenu({ editor, view, state }: ShouldShowFloatingMenuArgs): boolean {
  if (!editor?.isEditable) return false
  if (!view?.hasFocus()) return false

  const { selection } = state
  // A non-empty selection is the bubble menu's territory.
  if (!selection.empty) return false
  // Node selections (an image, a horizontal rule) aren't a text caret, so there's nowhere to insert.
  if (!(selection instanceof TextSelection)) return false

  const { $anchor } = selection
  if (!$anchor.parent.isTextblock) return false
  // Code blocks take their content literally — a placeholder chip has no meaning inside one.
  if ($anchor.parent.type.spec.code) return false

  return true
}
